import ColorThief from 'colorthief';

interface ExtractedColors {
  dominant: string;
  palette: string[];
  isDark: boolean;
}

interface ColorExtractionResult {
  colors: ExtractedColors;
  backgroundGradient: string;
  textColor: string;
  accentColor: string;
}

/**
 * STABLE CORS-free artwork color extraction system
 * Strategy: Download artwork via fetch with explicit Origin header, then extract colors locally
 * NO MORE crossOrigin = "Anonymous" - that causes inconsistent CORS issues!
 */
export class ArtworkColorExtractor {
  private static cache = new Map<string, ColorExtractionResult>();
  private static colorThief = new ColorThief();

  /**
   * Main function: Extract colors from artwork URL with stable CORS handling
   */
  static async extractColors(artworkUrl: string): Promise<ColorExtractionResult> {
    // Check cache first
    if (this.cache.has(artworkUrl)) {
      console.log('🚀 Using cached colors for:', artworkUrl);
      return this.cache.get(artworkUrl)!;
    }

    try {
      // Step 1: Use stable fetch + blob URL approach (NO crossOrigin issues)
      const colors = await this.extractColorsViaBlobUrl(artworkUrl);
      
      // Step 2: Generate gradient and text colors
      const result = this.generateColorScheme(colors);
      
      // Step 3: Cache result
      this.cache.set(artworkUrl, result);
      
      console.log('🎨 Colors extracted successfully:', result);
      return result;
      
    } catch (error) {
      console.warn('⚠️ Color extraction failed, using fallback colors:', error);
      return this.getFallbackColors();
    }
  }

  /**
   * STABLE CORS-free color extraction using fetch + blob URL
   * This is MORE RELIABLE than crossOrigin = "Anonymous"
   */
  private static async extractColorsViaBlobUrl(imageUrl: string): Promise<ExtractedColors> {
    return new Promise(async (resolve, reject) => {
      try {
        // Step 1: Fetch image as blob with explicit Origin header
        console.log('🌐 Fetching image as blob (stable CORS):', imageUrl);
        const response = await fetch(imageUrl, {
          mode: 'cors',
          cache: 'no-cache', // Force fresh request with Origin header
          headers: {
            'Origin': window.location.origin // Explicitly set Origin header
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        
        // Step 2: Load blob URL in image element (NO CORS issues!)
        const img = new Image();
        
        img.onload = () => {
          try {
            // Extract colors with ColorThief
            const dominantRgb = this.colorThief.getColor(img);
            const dominant = this.rgbToHex(dominantRgb);

            const paletteRgb = this.colorThief.getPalette(img, 6) || [dominantRgb];
            const palette = paletteRgb.map(rgb => this.rgbToHex(rgb));

            const isDark = this.isColorDark(dominantRgb);

            console.log('✅ Stable ColorThief extraction successful');
            
            // Cleanup blob URL
            URL.revokeObjectURL(blobUrl);
            
            resolve({
              dominant,
              palette,
              isDark
            });
          } catch (error) {
            console.error('❌ ColorThief extraction failed:', error);
            URL.revokeObjectURL(blobUrl);
            reject(error);
          }
        };

        img.onerror = (error) => {
          console.warn('⚠️ Blob image load failed:', error);
          URL.revokeObjectURL(blobUrl);
          reject(new Error('Blob image failed to load'));
        };

        // Load from blob URL (NO CORS issues!)
        img.src = blobUrl;
        
      } catch (fetchError) {
        console.error('❌ Fetch failed:', fetchError);
        reject(fetchError);
      }
    });
  }

  /**
   * Generate complete color scheme from extracted colors
   * Desktop-style approach with proper opacity values
   */
  private static generateColorScheme(colors: ExtractedColors): ColorExtractionResult {
    const { dominant, palette, isDark } = colors;

    // Desktop approach: Create darker and lighter variations of dominant color
    const dominantRgb = this.hexToRgb(dominant);
    if (!dominantRgb) {
      return this.getFallbackColors();
    }
    
    const darkerColor = this.rgbToHex([
      Math.max(0, dominantRgb.r - 50),
      Math.max(0, dominantRgb.g - 50),
      Math.max(0, dominantRgb.b - 50)
    ]);
    
    const lighterColor = this.rgbToHex([
      Math.min(255, dominantRgb.r + 20),
      Math.min(255, dominantRgb.g + 20),
      Math.min(255, dominantRgb.b + 20)
    ]);
    
    // Match desktop player opacity values for darker, richer background
    // Desktop uses: 1.0, 0.8, 0.6 = FF, CC, 99 (but make it even darker for web)
    const backgroundGradient = `linear-gradient(135deg, ${darkerColor}F0 0%, ${dominant}D9 50%, ${lighterColor}B3 100%)`;

    // Determine text color based on background  
    const textColor = isDark ? '#ffffff' : '#1f2937';

    // Select accent color (most vibrant from palette)
    const accentColor = this.selectAccentColor(palette, isDark);

    return {
      colors,
      backgroundGradient,
      textColor,
      accentColor
    };
  }

  /**
   * Select best accent color from palette
   */
  private static selectAccentColor(palette: string[], isDark: boolean): string {
    if (palette.length === 0) return '#6b7280';
    
    // Find the most vibrant color (high saturation + appropriate brightness)
    const sortedByVibrancy = palette
      .map(hex => ({ 
        hex, 
        brightness: this.getColorBrightness(hex),
        saturation: this.getColorSaturation(hex)
      }))
      .sort((a, b) => {
        // For dark backgrounds, prefer bright + saturated colors
        // For light backgrounds, prefer darker + saturated colors
        const scoreA = isDark 
          ? a.saturation + a.brightness
          : a.saturation + (255 - a.brightness);
        const scoreB = isDark 
          ? b.saturation + b.brightness
          : b.saturation + (255 - b.brightness);
        return scoreB - scoreA;
      });

    return sortedByVibrancy[0]?.hex || palette[0];
  }

  /**
   * Get color saturation (0-100)
   */
  private static getColorSaturation(hex: string): number {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return 0;
    
    const max = Math.max(rgb.r, rgb.g, rgb.b);
    const min = Math.min(rgb.r, rgb.g, rgb.b);
    
    if (max === 0) return 0;
    return ((max - min) / max) * 100;
  }

  /**
   * Get color brightness (0-255)
   */
  private static getColorBrightness(hex: string): number {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return 0;
    
    // Luminance formula
    return (rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114);
  }

  /**
   * Check if RGB color is dark
   */
  private static isColorDark(rgb: number[]): boolean {
    const brightness = (rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114);
    return brightness < 128;
  }

  /**
   * Convert RGB array to hex string
   */
  private static rgbToHex(rgb: number[]): string {
    const [r, g, b] = rgb;
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }

  /**
   * Convert hex to RGB object (public helper for external use)
   */
  static hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  /**
   * Get fallback colors when extraction fails
   */
  private static getFallbackColors(): ColorExtractionResult {
    return {
      colors: {
        dominant: '#374151',
        palette: ['#374151', '#4b5563', '#6b7280'],
        isDark: true
      },
      backgroundGradient: 'linear-gradient(135deg, #374151F0 0%, #4b5563D9 50%, #6b7280B3 100%)',
      textColor: '#ffffff',
      accentColor: '#6b7280'
    };
  }

  /**
   * Clear cache (for memory management)
   */
  static clearCache(): void {
    this.cache.clear();
  }

  /**
   * Validate if URL is a valid image
   */
  static isValidImageUrl(url: string): boolean {
    if (!url) return false;
    
    try {
      new URL(url);
    } catch {
      return false;
    }

    const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
    const lowercaseUrl = url.toLowerCase();
    
    return imageExtensions.some(ext => lowercaseUrl.includes(ext));
  }
}

export type { ExtractedColors, ColorExtractionResult };